import { apiClient } from "./client";
import type { PilotLead } from "@/lib/pilot";
import { reviewEmailChange, type ReviewDraft, type ReviewSelection } from "@/lib/pilotReview";

type EditorialRead = { revision: number; people: PilotLead[]; affected_emails: number };
const endpoint = (slug: string, campaignId: number) =>
  `/projects/${slug}/email/campaigns/${campaignId}/editorial`;

export async function getPilotEditorial(slug: string, campaignId: number): Promise<EditorialRead> {
  return (await apiClient.get<EditorialRead>(endpoint(slug, campaignId))).data;
}

/** The existing versioned draft-only endpoint; never syncs or executes a lead. */
export async function savePilotReviewEmail(slug: string, campaignId: number, draft: ReviewDraft): Promise<EditorialRead> {
  const path = endpoint(slug, campaignId);
  const change = reviewEmailChange(draft);
  await apiClient.post<EditorialRead>(`${path}/preview`, change);
  return (await apiClient.patch<EditorialRead>(path, change)).data;
}


export type MessageVersion = {
  id: string; revision: number; at: string | null; author: string | null; author_id: string | null;
  actor_type: string; origin: string; description: string | null; subject: string; body: string; individual: boolean; current: boolean;
};
export type ReviewMessage = {
  revision: number; person_code: string; position: number; variant: "A" | "B" | null;
  assigned_variant: "A" | "B"; subject: string; body: string;
  history: MessageVersion[]; unavailable_history: boolean;
};

/** Read-only rendering from the same Sequence models and stored prepared values. */
export async function getPilotReviewMessage(slug: string, campaignId: number, selection: ReviewSelection): Promise<ReviewMessage> {
  return (await apiClient.get<ReviewMessage>(`${endpoint(slug, campaignId)}/emails/${encodeURIComponent(selection.code)}/${selection.position}`,
    {params: selection.position === 1 ? {variant: selection.variant} : {}})).data;
}

export async function restorePilotReviewMessage(slug: string, campaignId: number, message: ReviewMessage, version: string) {
  return (await apiClient.patch<EditorialRead>(endpoint(slug, campaignId), {
    scope: "restore_email", expected_revision: message.revision, person_code: message.person_code,
    position: message.position, ...(message.position === 1 ? {variant: message.variant} : {}), restore_version: version,
  })).data;
}

export type ReviewComment = {
  id: string; body: string; author_id: string; author: string; at: string;
  revision: number; message_version: string; text_hash: string; previous_version: boolean;
};
export type ReviewComments = {
  person_code: string; position: number; variant: "A" | "B" | null;
  text_hash: string; can_comment: boolean; comments: ReviewComment[];
};
const commentsEndpoint = (slug: string, campaignId: number, selection: ReviewSelection) =>
  `${endpoint(slug, campaignId)}/emails/${encodeURIComponent(selection.code)}/${selection.position}/comments`;
export async function getPilotComments(slug: string, campaignId: number, selection: ReviewSelection): Promise<ReviewComments> {
  return (await apiClient.get<ReviewComments>(commentsEndpoint(slug, campaignId, selection),
    {params: selection.position === 1 ? {variant: selection.variant} : {}})).data;
}
export async function addPilotComment(slug: string, campaignId: number, selection: ReviewSelection,
  body: string, textHash: string, requestId: string): Promise<ReviewComments> {
  return (await apiClient.post<ReviewComments>(commentsEndpoint(slug, campaignId, selection),
    {body, text_hash: textHash, request_id: requestId},
    {params: selection.position === 1 ? {variant: selection.variant} : {}})).data;
}
